// @ts-nocheck
/**
 * tileAndDuplicate.ts
 * タイリング・複製・マーカー・データ作成メソッド
 * App.vue から切り出し
 */

import * as turf from '@turf/turf'
import maplibregl from 'maplibre-gl'
import {
  tileGenerateForUser,
  geojsonAddLayer,
  markerAddAndRemove,
  updateAllElevations,
  splitLineStringIntoPoints,
  fetchElevation,
  zahyokei,
  downloadGeoJSONAsCSV,
  getNowFileNameTimestamp,
  downloadTextFile,
} from '@/js/downLoad'
import { clickCircleSource } from '@/js/layers'
import { highlightSelectedFeature } from '@/js/draw'
import { geojsonCreate } from '@/js/pyramid'
import { escapeHtml } from '@/js/utils/sanitize'
import { getMdiIconPath, findStampById } from '@/js/stamps'
import { convertFromEPSG4326 } from '@/js/dxfExport'
import { geoJSONToSIMA } from '@/js/simaExport'
import { consumeToken } from '@/js/tokenService'
import store from '@/store'

type VM = any

export async function startTiling(vm: VM) {
  // ジオリファレンス保存: トークン消費（10pt）
  const tokenResult = await consumeToken('georef_save')
  if (!tokenResult.success && tokenResult.error !== 'NETWORK_ERROR_IGNORED') return

  vm.$store.state.transparent = '0'
  const isJpg = vm.$store.state.tiffAndWorldFile.find(f => f.name.includes('jgw'))
  if (isJpg) {
    tileGenerateForUser('jpg,jpeg','jgw',true, true)
  } else {
    tileGenerateForUser('png','pgw',true, true)
  }
  vm.showTileDialog = false
}


// 親コンポーネント methods 内に置く
export function openTileUploadDialog(vm: VM, payload) {
  const {
    kind,                 // 'similarity' | 'affine' | 'homography' | 'tps'
    fileOriginal,         // File|Blob （元画像。相似/アフィンで必須）
    worldFile,            // string     （ワールドファイル本文。相似/アフィンで必須）
    worldFileName,        // string     （例: image.jgw/pgw/tfw）無ければ推定
    affineM               // 予備（worldFileが無い時だけ最終手段で使う）
  } = payload || {};

  const baseName = vm.s_gazoName || 'image';

  // ヘルパ
  const extFromMime = (mime) => {
    if (!mime) return '.png';
    if (mime.includes('jpeg')) return '.jpg';
    if (mime.includes('png'))  return '.png';
    if (mime.includes('tiff')) return '.tif';
    return '.png';
  };
  const inferWorldExtByName = (name='') => {
    const ext = (name.split('.').pop() || '').toLowerCase();
    if (ext === 'png') return 'pgw';
    if (ext === 'tif' || ext === 'tiff') return 'tfw';
    return 'jgw'; // jpgなど
  };

  // File の体裁を保証
  const asFile = (blobOrFile, nameFallback) => {
    if (!blobOrFile) return null;
    if (blobOrFile instanceof File) return blobOrFile;
    const ext = extFromMime(blobOrFile.type);
    return new File([blobOrFile], `${nameFallback}${ext}`, { type: blobOrFile.type || 'application/octet-stream' });
  };

  // ========= 相似 / アフィン：元画像 + ワールドファイル =========
  if (kind === 'similarity' || kind === 'affine') {
    let imgFile = asFile(fileOriginal, baseName);
    if (!imgFile) {
      console.error('openTileUploadDialog: fileOriginal is required for affine/similarity');
      return;
    }

    // ワールドファイル本文を優先的に使う（子で計算済みの値が最も厳密）
    let wldText = worldFile;
    if (!wldText && affineM && typeof vm.generateWorldFile === 'function') {
      // 予備：どうしても無い場合のみ再生成（精度は子計算品に劣る可能性）
      wldText = vm.generateWorldFile(affineM);
    }
    if (!wldText) {
      console.error('openTileUploadDialog: worldFile (text) is required for affine/similarity');
      return;
    }

    const wldName = worldFileName || `${baseName}.${inferWorldExtByName(imgFile.name)}`;
    const wldFile = new File([wldText], wldName, { type: 'text/plain' });

    // ストア/ダイアログへ（既存の受け口に合わせて）
    vm.$store.commit('setTiffAndWorldFile', [imgFile, wldFile]);

    vm.showTileDialog = true;
    return;
  }

  // ========= TPS =========
  if ((kind === 'tps') && worldFile && fileOriginal) {

    // 子が「ワープ済み画像＋world file」を作ってくれたケース → そのまま通常経路へ
    const imgFile = asFile(fileOriginal, baseName);
    const wldName = worldFileName || `${baseName}.${inferWorldExtByName(imgFile.name)}`;
    const wldFile = new File([worldFile], wldName, { type: 'text/plain' });
    vm.$store.commit('setTiffAndWorldFile', [imgFile, wldFile]);
    vm.showTileDialog = true;
    return;
  }
  console.warn('openTileUploadDialog: unknown kind', kind);
}


export function updateMapMarkers(vm: VM) {
  const map = vm.$store.state.map01;

  // 既存マーカーを削除
  vm.mapCoordMarkers.forEach(marker => marker.remove());
  vm.mapCoordMarkers = [];

  vm.gcpList.forEach((gcp, index) => {
    if (!gcp.mapCoord) return;

    // 番号付きの要素を作る
    const el = document.createElement('div');
    el.innerText = String(index + 1);
    el.style.cssText = `
    background: blue;
    color: white;
    border-radius: 50%;
    width: 22px;
    height: 22px;
    text-align: center;
    line-height: 22px;
    font-size: var(--oh3-font-size-sm);
    font-weight: bold;
    box-shadow: 0 0 2px rgba(0,0,0,0.6);
    cursor: move;
  `;

    const marker = new maplibregl.Marker({
      element: el,
      anchor: 'center',
      draggable: true  // ← ドラッグ可能に
    })
        .setLngLat(gcp.mapCoord)
        .addTo(map);

    marker.on('dragend', () => {
      const lngLat = marker.getLngLat();
      gcp.mapCoord = [lngLat.lng, lngLat.lat];  // 🔥 GCPを更新！
    });

    vm.mapCoordMarkers.push(marker);
  });
}

export function onMapClick(vm: VM, e) {
  if (!vm.gcpList.length || vm.gcpList.length === 0) {
    return;
  }
  const last = vm.gcpList.find(gcp => gcp.mapCoord === null);
  if (!last) {
    vm.$store.dispatch('messageDialog/open', {
      id: 'global',
      title: 'ジオリファレンス',
      contentHtml: '<p>先に画像側で対応点をクリックしてください</p>',
      options: { icon: 'mdi-image-marker', iconColor: 'warning' }
    })
    return;
  }
  const lngLat = [e.lngLat.lng, e.lngLat.lat];
  last.mapCoord = lngLat;

}

export function test0(vm: VM) {
  const map = vm.$store.state.map01
  vm.$store.state.isMoveMode = !vm.$store.state.isMoveMode;
  // 初期化時に移動モードに応じてインタラクションを設定
  function updateMapInteractions() {
    if (vm.$store.state.isMoveMode) {
      map.dragPan.disable(); // パンを無効化
      map.touchZoomRotate.disable(); // タッチでのズーム/回転を無効化
      map.scrollZoom.disable(); // スクロールズームを無効化
    } else {
      alert('pan')
      map.dragPan.enable(); // パンを有効化
      map.touchZoomRotate.enable(); // タッチでのズーム/回転を有効化
      map.scrollZoom.enable(); // スクロールズームを有効化
    }
  }
  updateMapInteractions()
}

// 複製モード時のクリック処理：複製元の地物をクリック位置に複製
// 連続複製モード：クリックするたびに複製、ESCで終了
export function executeDuplicate(vm: VM, lng, lat) {
  const sourceFeature = vm.$store.state.duplicateSourceFeature
  if (!sourceFeature) {
    vm.$store.commit('setDrawDuplicate', false)
    return
  }

  const map = vm.$store.state.map01
  if (!map) {
    vm.$store.commit('setDrawDuplicate', false)
    return
  }

  // 新しいIDを生成
  const newId = String(Math.floor(10000 + Math.random() * 90000))

  // 元の地物のジオメトリタイプに応じて座標を計算
  const geomType = sourceFeature.geometry.type
  let newCoordinates

  if (geomType === 'Point') {
    // ポイントの場合はそのままクリック位置に配置
    newCoordinates = [lng, lat]
  } else if (geomType === 'LineString') {
    // ラインの場合：元の地物の重心からクリック位置へのオフセットを計算し、全ての座標に適用
    const originalCoords = sourceFeature.geometry.coordinates
    const centroid = vm.calculateCentroid(originalCoords)
    const offsetLng = lng - centroid[0]
    const offsetLat = lat - centroid[1]
    newCoordinates = originalCoords.map(coord => [coord[0] + offsetLng, coord[1] + offsetLat])
  } else if (geomType === 'Polygon') {
    // ポリゴンの場合：元の地物の重心からクリック位置へのオフセットを計算
    const originalCoords = sourceFeature.geometry.coordinates[0]
    const centroid = vm.calculateCentroid(originalCoords)
    const offsetLng = lng - centroid[0]
    const offsetLat = lat - centroid[1]
    // ポリゴンは[[...]]形式
    newCoordinates = [originalCoords.map(coord => [coord[0] + offsetLng, coord[1] + offsetLat])]
  }

  // プロパティをコピーして新しいIDとpairIdを設定
  // pairIdも更新しないと、元の地物を移動したとき複製も一緒に移動してしまうバグが発生する
  const newProperties = { ...sourceFeature.properties, id: newId, pairId: newId }

  // 円の場合は特殊処理が必要
  const isCircle = sourceFeature.properties.radius !== undefined
  const geoType = isCircle ? 'Circle' : geomType

  // geojsonCreateで地物を作成
  if (isCircle) {
    // 円の場合はCircleとして再作成
    const radius = sourceFeature.properties.radius
    vm.$store.state.currentCircleRadius = radius
    geojsonCreate(map, 'Circle', [lng, lat], newProperties)
  } else {
    geojsonCreate(map, geoType, newCoordinates, newProperties)
  }

  // 連続複製モード：複製モードは継続、ESCで終了
  // FloatingWindowは開かない（連続複製中は編集しない）

  // ポイントでスタンプ（アイコン）がある場合、マーカーを作成
  if (geomType === 'Point' && sourceFeature.properties?.stamp) {
    vm.createStampMarkerForDuplicate(newId, newCoordinates, newProperties, map)
  }

  // 新しい地物を選択状態にして強調表示
  setTimeout(() => {
    const source = map.getSource('click-circle-source')
    if (source) {
      const geojson = source._data?.geojson || source._data
      if (geojson && geojson.features) {
        const newFeature = geojson.features.find(f => f.properties?.id === newId)
        if (newFeature) {
          // 強調表示（geojsonCreate後に実行）
          highlightSelectedFeature(map, newId)
        }
      }
    }
  }, 300)
}

// 座標配列の重心を計算
export function calculateCentroid(coordinates) {
  const sumLng = coordinates.reduce((sum, c) => sum + c[0], 0)
  const sumLat = coordinates.reduce((sum, c) => sum + c[1], 0)
  return [sumLng / coordinates.length, sumLat / coordinates.length]
}

// 複製時にスタンプマーカーを作成（restoreStampsFromGeojsonと同じロジック）
export function createStampMarkerForDuplicate(vm: VM, newId, coords, props, map) {
  const el = document.createElement('div')
  el.className = 'stamp-marker'

  if (props.stampText) {
    el.innerHTML = `
      <div class="stamp-number-marker" style="color: ${escapeHtml(props.stampColor)}; border-color: ${escapeHtml(props.stampColor)};">
        ${escapeHtml(props.stampText)}
      </div>
    `
  } else {
    // 動的にSVGパスを解決（stampId優先、なければstampPathにフォールバック）
    let svgPath = props.stampPath
    if (props.stampId) {
      const mdiPath = getMdiIconPath(props.stampId)
      if (mdiPath) {
        svgPath = mdiPath
      } else {
        const stamp = findStampById(props.stampId)
        if (stamp?.path) {
          svgPath = stamp.path
        } else if (stamp?.mdiIcon) {
          svgPath = getMdiIconPath(stamp.mdiIcon)
        }
      }
    }
    el.innerHTML = `
      <svg viewBox="0 0 24 24" width="32" height="32">
        <path fill="${props.stampColor}" d="${svgPath}" />
      </svg>
    `
  }
  el.style.cursor = 'pointer'
  el.title = props.stampLabel

  const marker = new maplibregl.Marker({
    element: el,
    anchor: 'center',
    draggable: false  // 直接ドラッグ無効（移動は編集モードで行う）
  })
    .setLngLat([coords[0], coords[1]])
    .addTo(map)

  marker._stampFeatureId = newId
  marker._stampData = {
    id: props.stampId,
    label: props.stampLabel,
    color: props.stampColor,
    text: props.stampText,
    path: props.stampPath,
  }

  // ダブルクリックで削除（無効化）
  // el.addEventListener('dblclick', (e) => {
  //   e.stopPropagation()
  //   marker.remove()
  //   if (vm._restoredStampMarkers) {
  //     vm._restoredStampMarkers = vm._restoredStampMarkers.filter(m => m !== marker)
  //   }
  //   featuresDelete([newId])
  // })

  // クリックでポイントFWを開く
  el.addEventListener('click', (e) => {
    e.stopPropagation()
    const currentSource = map.getSource('click-circle-source')
    if (!currentSource) return
    const currentGeojson = vm.getSourceData(currentSource)
    if (!currentGeojson?.features) return
    const clickedFeature = currentGeojson.features.find(f => f.properties?.id === newId)
    if (clickedFeature) {
      import('@/js/draw').then(({ highlightSelectedFeature }) => {
        highlightSelectedFeature(map, newId)
      })
      vm.$store.commit('setCurrentDrawFeature', clickedFeature)
      vm.$store.commit('setFloatingVisible', { id: 'draw-floating-window', visible: true })
      // DrawDrawerも開く
      // setCurrentDrawFeatureがshowDrawDrawer=falseにリセットするため、nextTickで開く
      if (clickedFeature.geometry?.type === 'Point' && !clickedFeature.properties?.isCircleCenter && clickedFeature.properties?.type !== 'Text') {
        vm.$store.state.drawFeature = clickedFeature
        if (!vm.$store.state.isSmall500 || !vm.$store.state.isDraw) {
          Promise.resolve().then(() => {
            vm.$store.commit('setDrawDrawer', true)
          })
        }
      }
    }
  })

  // マーカーを登録
  if (!vm._restoredStampMarkers) {
    vm._restoredStampMarkers = []
  }
  vm._restoredStampMarkers.push(marker)

  if (!vm.$store.state.stampMarkers) {
    vm.$store.state.stampMarkers = {}
  }
  vm.$store.state.stampMarkers[newId] = marker
}

// Featureから直接標高図を作成（確認ダイアログなし）
export function createElevationFromFeature(vm: VM, feature) {
  if (!feature || !feature.geometry) return
  const geomType = feature.geometry.type
  if (geomType !== 'LineString' && geomType !== 'MultiLineString') return

  // tgtFeatureを設定して標高図作成
  vm.$store.state.tgtFeature = feature
  vm.elevationCreate()
}

// 既存の線から標高図を作成
export function elevationCreate(vm: VM) {
  vm.$store.state.pointSima = false
  const segments = vm.$store.state.segments || vm.segments || 100
  const pointGeojson = splitLineStringIntoPoints(vm.$store.state.tgtFeature, segments)
  async function updateAllElevations() {
    await Promise.all(pointGeojson.features.map(async (feature) => {
      const [lon, lat] = feature.geometry.coordinates;
      const z = await fetchElevation(lon, lat);
      feature.geometry.coordinates[2] = Number(z);
    }));
    vm.$store.state.elevationGeojson = pointGeojson
    // FloatingWindowで標高図を表示
    vm.$store.commit('setFloatingVisible', {
      id: 'elevation-dialog',
      visible: true
    })
  }
  updateAllElevations();
}

export function pointCsvCreate(vm: VM) {
  vm.$store.state.pointSima = false
  const pointGeojson = splitLineStringIntoPoints(vm.$store.state.tgtFeature, vm.segments)
  async function updateAllElevations() {
    await Promise.all(pointGeojson.features.map(async (feature) => {
      const [lon, lat] = feature.geometry.coordinates;
      const z = await fetchElevation(lon, lat);
      feature.geometry.coordinates[2] = Number(z);
    }));
    const epsg = zahyokei.find(item => item.kei === store.state.zahyokei).code
    const convertedGeojson = convertFromEPSG4326(pointGeojson, epsg)
    downloadGeoJSONAsCSV(convertedGeojson,getNowFileNameTimestamp() + '.csv')
  }
  updateAllElevations();
}

export function pointSimaCreate(vm: VM) {
  vm.$store.state.pointSima = false
  const pointGeojson = splitLineStringIntoPoints(vm.$store.state.tgtFeature, vm.segments)
  async function updateAllElevations() {
    await Promise.all(pointGeojson.features.map(async (feature) => {
      const [lon, lat] = feature.geometry.coordinates;
      const z = await fetchElevation(lon, lat);
      feature.geometry.coordinates[2] = Number(z);
    }));
    const epsg = zahyokei.find(item => item.kei === store.state.zahyokei).code
    const convertedGeojson = convertFromEPSG4326(pointGeojson, epsg)
    const simaText = geoJSONToSIMA(convertedGeojson)
    downloadTextFile(`${getNowFileNameTimestamp()}.sim`, simaText, 'shift-jis')
  }
  updateAllElevations();
}

